import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

import { NoData } from "@/components/common/section-components/NoData";

import {
  clearFiltersHandler,
  fetchCheckRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { formatCurrency } from "@/utils/format-currency";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { PendingMessagesAlert } from "@/components/common/section-components/alert/PendingMessagesAlert";
import { CashRequestStatus } from "@/constants/entity-status";
import dayjs from "dayjs";
import {
  CheckRequestEntity,
  checkRequestFilterSchema,
} from "./schema/check-requests.schema";
import { CheckRequestHeaderElements } from "./schema/header-elements";
import { CheckRequestFilter } from "@/components/common/section-components/filters/CheckRequestFilter";
import { CheckRequestActions } from "@/components/common/section-components/action/CheckRequestActions";
import { usePolling } from "@/hooks/usePolling";
import { useAuth } from "@/hooks/useAuth";

export const CheckRequestPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingCheckRequests, setLoadingCheckRequests] =
    useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [currentFilters, setCurrentFilters] = useState({});

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const { handleSubmit, getValues, setValue, control } = useForm({
    resolver: zodResolver(checkRequestFilterSchema),
  });
  const [page, setPage] = useState(1);

  const { fetchPermissions } = useRoles();

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchCheckRequests,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
    });
  };

  const navigate = useNavigate();

  const [checkRequests, setCheckRequests] = useState<{
    listado: CheckRequestEntity[];
    totalPages: number;
    totalRecords: number;
    pendingRequests: boolean;
  }>({
    listado: [],
    totalPages: 1,
    totalRecords: 10,
    pendingRequests: false,
  });
  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;
  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchCheckRequests({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.CHECK_REQUEST_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <CheckRequestActions handleDetail={handleDetail} item={item} />
  );

  const { user } = useAuth();
  const fetchCheckRequests = async (filters?: any, showLoad = true) => {
    await fetchCheckRequestsHandler({
      showLoad,
      setLoadingCheckRequests,
      setCheckRequests,
      filters,
      user,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { state } = useLocation();

  useEffect(() => {
    if (isActive) {
      const restoredPage = state?.page;
      if (restoredPage) {
        setPage(restoredPage);
        fetchCheckRequests({ ...currentFilters, "P.Pagina": restoredPage });
      } else {
        fetchCheckRequests();
      }
    }
  }, [isActive]);

  usePolling({
    callback: () =>
      fetchCheckRequests({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive: isActive && !loadingCheckRequests,
  });

  const { userPermissions } = useRoles();

  const handleAdd = () => {
    navigate(AppRoutes.CHECK_REQUEST_CREATION);
  };

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.CHECK_REQUEST && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={hasWriteAccess}
        title="Cheque"
        loading={loadingCheckRequests}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
      />

      <Flex>
        {checkRequests?.pendingRequests && !loadingCheckRequests && (
          <PendingMessagesAlert
            submitFilters={submitFilters}
            setValue={setValue}
          />
        )}
        <CheckRequestFilter
          handleSubmit={handleSubmit}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          clearFilters={clearFilters}
          filtersApplied={filtersApplied}
          submitFilters={submitFilters}
          control={control}
          disableFilters={loadingCheckRequests}
        />
      </Flex>

      <Flex flexDir="column">
        {Boolean(!loadingCheckRequests && !checkRequests?.listado?.length) && (
          <NoData />
        )}
        {Boolean(!loadingCheckRequests && checkRequests?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                checkRequests?.listado?.map((el: CheckRequestEntity) => ({
                  ...el,
                  fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                  monto: `$${formatCurrency(el?.monto)}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as CashRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={CheckRequestHeaderElements}
              actionElements={getActionElements}
            />
            {checkRequests?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={checkRequests?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
